import React, {useState} from "react";
import {Form, Input, Radio, Switch, Tooltip} from "antd";
import {Slide} from "@reside/forms/dist/templateTypes";
import {styled} from "@reside/ui";

import {
  focusedAtomAtom,
  useFocusedNode,
  useSetFocusedNodePropertyValue,
} from "../../model/editor";
import {useRecoilValue} from "recoil";
import {
  useTemplateAtomState,
  useIsResidentExperienceTemplateValue,
} from "../../model/template";
import {SourceSlide} from "../../model/schemaTypes";

export const SlideForm = () => {
  const node = useFocusedNode() as Slide;

  const isResidentExperienceTemplateValue = useIsResidentExperienceTemplateValue();

  return isResidentExperienceTemplateValue ? (
    <Form
      key={node.id}
      initialValues={{
        inCompletionChecklist: node.inCompletionChecklist,
        excludeFromTOC: node.excludeFromTOC,
        infoMessage: node.infoMessage?.translationKey,
        backgroundUrl: node.backgroundUrl?.replace("/assets/", ""),
        darkBackground: node.darkBackground,
        verticalContentAlignment: node.verticalContentAlignment,
      }}
    >
      <InCompletionChecklistAttribute />
      <ExcludeFromTOCAttribute />
      <InfoMessageAttribute />
      <VerticalContentAlignmentAttribute />
      <DarkBackgroundAttribute />
      <BackgroundUrlAttribute />
    </Form>
  ) : null;
};

const InCompletionChecklistAttribute = () => {
  const node = useFocusedNode() as Slide;
  const setValue = useSetFocusedNodePropertyValue("inCompletionChecklist");

  return (
    <Tooltip title='If checked, this slide will appear in the "Completion Checklist" component'>
      <Form.Item name="inCompletionChecklist" label="In Completion Checklist">
        <Switch
          checked={node.inCompletionChecklist}
          onChange={enabled => setValue(enabled)}
        />
      </Form.Item>
    </Tooltip>
  );
};

const ExcludeFromTOCAttribute = () => {
  const node = useFocusedNode() as Slide;
  const setValue = useSetFocusedNodePropertyValue("excludeFromTOC");

  return (
    <Tooltip title="If checked, this slide won't be part of the Table of Contents">
      <Form.Item name="excludeFromTOC" label="Exclude slide from ToC">
        <Switch
          checked={node.excludeFromTOC}
          onChange={enabled => setValue(enabled)}
        />
      </Form.Item>
    </Tooltip>
  );
};

const DarkBackgroundAttribute = () => {
  const node = useFocusedNode() as Slide;
  const setValue = useSetFocusedNodePropertyValue("darkBackground");

  return node.backgroundUrl ? (
    <Form.Item name="darkBackground" label="Dark Background">
      <Switch
        checked={node.darkBackground}
        onChange={enabled => setValue(enabled)}
      />
    </Form.Item>
  ) : null;
};

const InfoMessageAttribute = () => {
  const node = useFocusedNode() as Slide;
  const setValue = useSetFocusedNodePropertyValue("infoMessage.translationKey");

  return (
    <Tooltip title='Hint message to be displayed on top of the slide. E.g. "Fill in the form"'>
      <Form.Item name="infoMessage" label="Info Message">
        <Input
          onChange={event => {
            event.target.value === ""
              ? setValue(undefined)
              : setValue(event.target.value);
          }}
          value={node.infoMessage?.translationKey}
          placeholder='E.g. "Fill in the form"'
        />
      </Form.Item>
    </Tooltip>
  );
};

const BackgroundUrlAttribute = ({
  backgrounds,
}: {
  backgrounds: ReadonlyArray<string>;
}) => {
  const [focusedNode, setFocusedNode] = useTemplateAtomState(
    useRecoilValue(focusedAtomAtom)!,
  );
  const setBackgroundUrl = useSetFocusedNodePropertyValue("backgroundUrl");
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(expanded => !expanded);

  return (
    <>
      <Form.Item style={{marginBottom: 0}} label={"Background Image"}>
        <ToggleButton onClick={toggleExpanded}>
          {expanded ? "Show less" : "Show more"}
        </ToggleButton>
      </Form.Item>
      {expanded && (
        <Form.Item name="backgroundUrl">
          <Radio.Group>
            <Radio
              value={undefined}
              onChange={() => {
                /**
                 * Delete attributes instead setting them to false.
                 */
                const {
                  backgroundUrl,
                  darkBackground,
                  ...rest
                } = focusedNode as SourceSlide;

                setFocusedNode(rest);
              }}
            >
              <Tooltip title="No background">
                <img
                  alt=""
                  width={64}
                  height={64}
                  style={{margin: 10}}
                  src="data:image/png;base64,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"
                />
              </Tooltip>
            </Radio>
            {backgrounds.map((name, index) => (
              <Radio
                key={index}
                value={name}
                onChange={event =>
                  setBackgroundUrl(`/assets/${event.target.value}`)
                }
              >
                <img
                  alt=""
                  width={64}
                  height={64}
                  style={{margin: 10}}
                  src={`/assets/${name}`}
                />
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      )}
    </>
  );
};

const ToggleButton = styled.span`
  color: #1890ff;
  font-weight: bold;
  margin-left: 10px;
  &:hover {
    opacity: 0.8;
    cursor: pointer;
  }
`;

BackgroundUrlAttribute.defaultProps = {
  backgrounds: [
    "welcome-bg.jpg",
    "bg-getstarted4.jpg",
    "img1@2x.jpg",
    "img2@2x.jpg",
    "img3@2x.jpg",
    "img4@2x.jpg",
    "img5@2x.jpg",
    "img6@2x.jpg",
  ],
};

const VerticalContentAlignmentAttribute = ({
  alignments,
}: {
  alignments: ReadonlyArray<string>;
}) => {
  const node = useFocusedNode() as Slide;
  const setValue = useSetFocusedNodePropertyValue("verticalContentAlignment");

  return (
    <Form.Item
      name="verticalContentAlignment"
      label="Vertical Content Alignment"
      wrapperCol={{span: 24}}
      labelCol={{span: 24}}
    >
      <Radio.Group
        onChange={event => {
          event.target.value === ""
            ? setValue(undefined)
            : setValue(event.target.value);
        }}
        value={node.verticalContentAlignment}
      >
        {alignments.map((alignment, index) => (
          <Radio.Button key={index} value={alignment}>
            {alignment}
          </Radio.Button>
        ))}
      </Radio.Group>
    </Form.Item>
  );
};

VerticalContentAlignmentAttribute.defaultProps = {
  alignments: ["top", "center", "bottom"],
};
